import type { SVGProps } from 'react';

/**
 * Icônes inline, tracées à la main.
 *
 * Grille commune de 24 px, trait de 1,75 px et extrémités arrondies pour
 * garder la même finesse dans les champs, les boutons et la navigation.
 *
 * Toutes sont dimensionnées en `currentColor` et `1em` : elles héritent de la
 * couleur et de la taille du texte environnant.
 */

type IconProps = SVGProps<SVGSVGElement>;

const base = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export const MailIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="2.5" y="4.5" width="19" height="15" rx="3" />
    <path d="m3.5 7 7.34 5.13a2 2 0 0 0 2.32 0L20.5 7" />
  </svg>
);

export const CalendarIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M7 3v4m10-4v4M3 10h18m-13 4h2m4 0h2m-8 3h2" />
  </svg>
);

export const LockIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="4" y="10.5" width="16" height="10.5" rx="3" />
    <path d="M8 10.5V7.5a4 4 0 1 1 8 0v3" />
  </svg>
);

export const EyeIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOffIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M9.9 5.8A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.85 3.75M6.35 7.9A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9.5 9.5 0 0 0 3.35-.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="m3.5 3.5 17 17" />
  </svg>
);

export const ArrowRightIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4.5 12h15" />
    <path d="m13.5 6 6 6-6 6" />
  </svg>
);

export const CheckIcon = (props: IconProps) => (
  <svg {...base} strokeWidth={3} {...props}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </svg>
);

export const AlertCircleIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5" />
    <circle cx="12" cy="16" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

export const ChevronDownIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="m6 9.5 6 6 6-6" />
  </svg>
);

export const LogOutIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M15 4.5h3a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-3" />
    <path d="M10.5 16 15 12l-4.5-4" />
    <path d="M15 12H4" />
  </svg>
);

export const LayoutGridIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
  </svg>
);

export const GlobeIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="9" />
    <ellipse cx="12" cy="12" rx="4" ry="9" />
    <path d="M3 12h18" />
  </svg>
);

export const UsersIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <circle cx="9" cy="8" r="3.25" />
    <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.2a3.25 3.25 0 0 1 0 5.6" />
    <path d="M17.5 14.6a5.5 5.5 0 0 1 3 4.9" />
  </svg>
);

export const BriefcaseIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="3" y="7.5" width="18" height="12" rx="2.5" />
    <path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5" />
    <path d="M3 12.5h18" />
  </svg>
);

export const GraduationCapIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="m2.5 8.5 9.5-5 9.5 5-9.5 5Z" />
    <path d="M6 10.5v6c3.5 3 8.5 3 12 0v-6M21.5 8.5v8" />
  </svg>
);

export const FileTextIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M14 3.5H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5Z" />
    <path d="M14 3.5v5h5" />
    <path d="M9 13h6M9 16.5h4" />
  </svg>
);

export const BuildingContactsIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M11 20.5H3.5V3.5H15v4" />
    <path d="M7 7.5h4M7 11.5h3M7 15.5h2" />
    <circle cx="17.5" cy="12" r="2.5" />
    <path d="M13.5 20.5v-1a4 4 0 0 1 8 0v1" />
  </svg>
);

export const ClipboardCheckIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    <rect x="8" y="2.5" width="8" height="5" rx="1.5" />
    <path d="m8 14 2.5 2.5L16 11" />
  </svg>
);

export const InvoiceIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M5 21V3l3 1.5L12 3l4 1.5L19 3v18l-3-1.5-4 1.5-4-1.5Z" />
    <path d="M8.5 8.5h7M8.5 12h7M12 15.5h3.5" />
  </svg>
);

export const TargetIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M20.5 10.5A8.5 8.5 0 1 1 13.5 3.5" />
    <path d="M16.5 11.5a4.5 4.5 0 1 1-4-4" />
    <path d="m12 12 8.5-8.5M16 3.5h4.5V8" />
  </svg>
);

export const TimesheetIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M9 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v3M7 2v4M16 2v4M3 9h14" />
    <path d="M7 13h1M7 16.5h1" />
    <circle cx="16.5" cy="16.5" r="5" />
    <path d="M16.5 13.5v3h2" />
  </svg>
);

export const FolderKanbanIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M3 7V5a2 2 0 0 1 2-2h4l3 3h7a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <path d="M7.5 10.5v6M12 10.5v3M16.5 10.5v4.5" />
  </svg>
);

export const SearchIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.5 15.5 4.5 4.5" />
  </svg>
);

export const CloseIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="m6 6 12 12M6 18 18 6" />
  </svg>
);

export const FilterIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M3.5 7h3m5 0h9M3.5 17h9m5 0h3" />
    <circle cx="9" cy="7" r="2.5" />
    <circle cx="15" cy="17" r="2.5" />
  </svg>
);

export const PlusIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const EditIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="m14.5 5.5 4 4M4 20l4.5-1 12-12a2.83 2.83 0 0 0-4-4l-12 12Z" />
    <path d="M13 20h7" />
  </svg>
);

export const PanelCollapseIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M9 4v16m7-11-3 3 3 3" />
  </svg>
);

export const PhoneIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M6.5 3.5h3l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 4.5 5.7 2 2 0 0 1 6.5 3.5Z" />
  </svg>
);

export const StarIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="m12 3.5 2.6 5.3 5.9.86-4.25 4.14 1 5.87L12 16.9l-5.25 2.77 1-5.87L3.5 9.66l5.9-.86Z" />
  </svg>
);

export const BuildingIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4 20.5V5a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 15 5v15.5" />
    <path d="M15 9.5h3.5A1.5 1.5 0 0 1 20 11v9.5" />
    <path d="M2.5 20.5h19" />
    <path d="M7.5 7.5h4M7.5 11.5h4M7.5 15.5h4" />
  </svg>
);

export const ChevronRightIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="m9.5 6 6 6-6 6" />
  </svg>
);

export const InboxIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M3.5 13.5h4l1.5 3h6l1.5-3h4" />
    <path d="M6 4.5h12l2.5 9v5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-5Z" />
  </svg>
);

export const UploadIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M12 16V4.5m-4 4 4-4 4 4" />
    <path d="M4 15v3.5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V15" />
  </svg>
);

/** Deux feuilles superposées : le doublon présumé d'une fiche. */
export const DuplicateIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="8.5" y="3.5" width="12" height="14" rx="2.5" />
    <path d="M15.5 20.5h-10a2 2 0 0 1-2-2v-11" />
  </svg>
);

/** Deux branches qui se rejoignent : la fusion de deux fiches. */
export const MergeIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M6 3.5v4a5 5 0 0 0 5 5h6" />
    <path d="M6 20.5v-4a5 5 0 0 1 5-5" />
    <path d="m14.5 9.5 3 3-3 3" />
  </svg>
);

export const TrashIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4.5 6.5h15M9.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v1.5" />
    <path d="M6.5 6.5 7.4 19a2 2 0 0 0 2 1.9h5.2a2 2 0 0 0 2-1.9l.9-12.5" />
  </svg>
);

export const SpinnerIcon = (props: IconProps) => (
  <svg {...base} {...props} className={`animate-spin ${props.className ?? ''}`}>
    <circle cx="12" cy="12" r="9" opacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" />
  </svg>
);

export const SettingsIcon = (props: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="m9.5 3-.6 2.2-2 .9-2-.6-2.5 4.3 1.6 1.6v2.3l-1.6 1.6 2.5 4.3 2-.6 2 .9.6 2.1h5l.6-2.1 2-.9 2 .6 2.5-4.3-1.6-1.6v-2.3l1.6-1.6-2.5-4.3-2 .6-2-.9L14.5 3z" />
    <circle cx="12" cy="12" r="3.3" />
  </svg>
);
